import { gql } from "@apollo/client"
import { ApiFormattedResponse } from "@models/api_formatted_response"
import { query } from "@utils/graphql"

// Local document until codegen is refreshed against an api-v2 that has Operator.
const ADMIN_GET_OPERATOR_RUN = gql`
  query adminGetOperatorRun($data: AdminOperatorRunInput!) {
    adminGetOperatorRun(data: $data) {
      success
      data
      code
      message
    }
  }
`

const ADMIN_OPERATOR_RUN_HEALTH = gql`
  query adminOperatorRunHealth($data: AdminOperatorRunHealthInput!) {
    adminOperatorRunHealth(data: $data) {
      success
      data
      code
      message
    }
  }
`

const ADMIN_LIST_OPERATOR_RUN_CHATS = gql`
  query adminListOperatorRunChats($data: AdminOperatorRunsInput!) {
    adminListOperatorRunChats(data: $data) {
      success
      data
      code
      message
    }
  }
`

const ADMIN_GET_OPERATOR_CHAT = gql`
  query adminGetOperatorChat($data: AdminOperatorChatInput!) {
    adminGetOperatorChat(data: $data) {
      success
      data
      code
      message
    }
  }
`

const ADMIN_LIST_OPERATOR_RUNS = gql`
  query adminListOperatorRuns($data: AdminOperatorRunsInput!) {
    adminListOperatorRuns(data: $data) {
      success
      data {
        items {
          id
          customerId
          cPersonId
          chatId
          kind
          request
          context
          decision
          tools
          status
          outcome
          outcomeReason
          error
          costUsd
          usage
          models
          durationMs
          engine
          customerName
          userName
          createdAt
        }
        hasMore
      }
      code
      message
    }
  }
`

export const ADMIN_OPERATOR_RUN_PROGRESS = gql`
  subscription adminOperatorRunProgress($data: AdminOperatorRunProgressInput!) {
    adminOperatorRunProgress(data: $data) {
      success
      data {
        customerId
        cPersonId
        chatId
        stage
        at
      }
    }
  }
`

export type AdminOperatorRunStatus = "RUNNING" | "SUCCESS" | "FAILED"

export const ADMIN_OPERATOR_RUN_OUTCOMES = [
  "ANSWERED",
  "UNANSWERED",
  "CLARIFIED",
  "PROPOSED",
  "ACTED"
] as const

export type AdminOperatorRunOutcome =
  (typeof ADMIN_OPERATOR_RUN_OUTCOMES)[number]

export type AdminOperatorRun = {
  id: string
  customerId: string
  cPersonId: string
  chatId: string
  kind: "MESSAGE" | "QUERY"
  request: string
  context?: { screen?: string; conversationId?: string } | null
  decision?: string | null
  tools: {
    name: string
    params?: unknown
    durationMs?: number
    ok: boolean
    records?: number
    error?: string
  }[]
  status: AdminOperatorRunStatus
  outcome?: AdminOperatorRunOutcome | null
  outcomeReason?: string | null
  error?: {
    code: string
    message: string
    stage: string
    stack?: string
  } | null
  costUsd: number
  usage?: {
    inputTokens: number
    cachedInputTokens: number
    outputTokens: number
  } | null
  models: string[]
  durationMs?: number | null
  engine?: { capability: string; version: string; source: string } | null
  customerName?: string | null
  userName?: string | null
  createdAt: string
}

export type AdminOperatorRunStep = {
  order: number
  kind: "MODEL" | "TOOL"
  name: string
  ok: boolean
  atMs: number
  durationMs: number
  prompt?: {
    name: string
    layer: string | null
    model: string
    schema?: string
    reasoning?: string
    maxTokens?: number
    images?: number
  }
  system?: string
  input?: unknown
  output?: unknown
  usage?: {
    inputTokens: number
    cachedInputTokens: number
    outputTokens: number
    costUsd: number
  }
  error?: string
  truncated?: string[]
}

export type AdminOperatorMessage = {
  id: string
  role: "user" | "operator"
  text?: string
  images?: { url: string; type: string }[]
  blocks: ({ k: string } & Record<string, unknown>)[]
  work?: { durationMs: number; steps: string[] }
  costUsd?: number
  createdAt: string
}

export type AdminOperatorRunDetail = {
  run: AdminOperatorRun
  steps: AdminOperatorRunStep[]
  chat: { id: string; title: string; messages: AdminOperatorMessage[] } | null
}

export type AdminOperatorRunHealth = {
  runs: number
  failed: number
  costUsd: number
  avgDurationMs: number
  byOutcome: { outcome: AdminOperatorRunOutcome | "NONE"; runs: number }[]
  errors: {
    code: string
    stage: string
    runs: number
    lastAt: string
    runId: string
    message: string
  }[]
  reasons: { reason: string; runs: number; lastAt: string; runId: string }[]
}

export type AdminOperatorRunsPage = {
  items: AdminOperatorRun[]
  hasMore: boolean
}

export type AdminOperatorRunsInput = {
  customerId?: string
  cPersonId?: string
  chatId?: string
  page: number
  limit: number
  status?: AdminOperatorRunStatus
  outcome?: AdminOperatorRunOutcome
  search?: string
  from?: string
  to?: string
}

export type AdminOperatorRunHealthInput = {
  customerId?: string
  cPersonId?: string
  from: string
  to?: string
}

export type AdminOperatorRunChat = {
  chatId: string
  lastRun: AdminOperatorRun
  runs: number
  failed: number
  running: number
  costUsd: number
}

export type AdminOperatorRunChatsPage = {
  items: AdminOperatorRunChat[]
  hasMore: boolean
}

export type AdminOperatorChatDetail = {
  chat: { id: string; title: string } | null
  messages: (AdminOperatorMessage & { runId?: string })[]
  runs: AdminOperatorRun[]
}

type JsonResponse<T> = {
  success?: boolean | null
  data?: T | null
  message?: string | null
}

async function jsonQuery<T>(
  document: typeof ADMIN_GET_OPERATOR_RUN,
  field: string,
  data: object
): Promise<ApiFormattedResponse<T>> {
  const response = await query<Record<string, JsonResponse<T>>>({
    query: document,
    variables: { data },
    fetchPolicy: "no-cache"
  })
  if (!response.isSuccess) {
    return { isSuccess: false, errorMessage: response.errorMessage }
  }
  const payload = response.data?.[field]
  if (!payload?.success || !payload.data) {
    return { isSuccess: false, errorMessage: payload?.message ?? undefined }
  }
  return { isSuccess: true, data: payload.data }
}

export const adminGetOperatorRunQuery = (runId: string) =>
  jsonQuery<AdminOperatorRunDetail>(
    ADMIN_GET_OPERATOR_RUN,
    "adminGetOperatorRun",
    { runId }
  )

export const adminOperatorRunHealthQuery = (
  data: AdminOperatorRunHealthInput
) =>
  jsonQuery<AdminOperatorRunHealth>(
    ADMIN_OPERATOR_RUN_HEALTH,
    "adminOperatorRunHealth",
    data
  )

export const adminListOperatorRunChatsQuery = (data: AdminOperatorRunsInput) =>
  jsonQuery<AdminOperatorRunChatsPage>(
    ADMIN_LIST_OPERATOR_RUN_CHATS,
    "adminListOperatorRunChats",
    data
  )

export const adminGetOperatorChatQuery = (chatId: string) =>
  jsonQuery<AdminOperatorChatDetail>(
    ADMIN_GET_OPERATOR_CHAT,
    "adminGetOperatorChat",
    { chatId }
  )

type AdminOperatorRunsResponse = {
  adminListOperatorRuns?: {
    success?: boolean | null
    data?: AdminOperatorRunsPage | null
    message?: string | null
  }
}

export async function adminListOperatorRunsQuery(
  data: AdminOperatorRunsInput
): Promise<ApiFormattedResponse<AdminOperatorRunsPage>> {
  const response = await query<AdminOperatorRunsResponse>({
    query: ADMIN_LIST_OPERATOR_RUNS,
    variables: { data },
    fetchPolicy: "no-cache"
  })
  if (!response.isSuccess) {
    return { isSuccess: false, errorMessage: response.errorMessage }
  }

  const payload = response.data?.adminListOperatorRuns
  if (!payload?.success || !payload.data) {
    return { isSuccess: false, errorMessage: payload?.message ?? undefined }
  }

  const page: AdminOperatorRunsPage = payload.data
  return { isSuccess: true, data: page }
}
